/**
 * レッスンから sandboxd を呼ぶ（lesson-format.md §7、sandboxd-client.md §4〜§5、observability.md §6）。
 * どれも `class.run`（purpose = lesson）の span の中で 1 回だけ `/api/run` を送る。
 * 書く課題（card なし）は Leitner に入れないので `class.card.grade` は作らない。
 */
import { judgeCases, type JudgeCase, type JudgeResult } from '../../lib/judge';
import { byteLength, ProtocolError, SandboxError, type RunResponse } from '../../lib/sandboxd';
import {
  recordRunOutcome,
  withSpan,
  type RunOutcome,
  type RunPurpose,
  type Verdict,
} from '../../lib/telemetry';
import { sandbox } from '../../services';
import type { RunHooks } from '../cards/run';

/** observability.md の `class.run.purpose`。基盤側の RunPurpose に 'lesson' が入るまでの橋渡し */
const LESSON_PURPOSE: RunPurpose = 'lesson';

function outcomeOf(e: unknown): RunOutcome | null {
  if (!(e instanceof SandboxError)) return e instanceof ProtocolError ? 'http_error' : null;
  switch (e.kind) {
    case 'busy':
      return 'busy';
    case 'timeout':
      return 'gateway_timeout';
    case 'network':
      return 'network_error';
    case 'local-busy':
      return null;
    default:
      return 'http_error';
  }
}

async function runLesson<T>(
  code: string,
  stdins: string[],
  hooks: RunHooks,
  interpret: (res: RunResponse) => { value: T; verdict?: Verdict },
): Promise<T> {
  return withSpan(
    'class.run',
    {
      'class.run.purpose': LESSON_PURPOSE,
      'class.run.cases': stdins.length,
      'class.run.code_bytes': byteLength(code),
    },
    async (span) => {
      let res: RunResponse;
      try {
        res = await sandbox.run(
          { code, stdins },
          {
            ...(hooks.signal ? { signal: hooks.signal } : {}),
            ...(hooks.onRetry ? { onRetry: hooks.onRetry } : {}),
          },
        );
      } catch (e) {
        const outcome = outcomeOf(e);
        if (outcome) recordRunOutcome(span, outcome);
        throw e;
      }
      try {
        const { value, verdict } = interpret(res);
        recordRunOutcome(span, 'completed', verdict);
        return value;
      } catch (e) {
        recordRunOutcome(span, 'http_error');
        throw e;
      }
    },
  );
}

/** 実行の結果を、実験・実行の表示に使う形にしたもの */
export type RunOutcomeView =
  | { kind: 'ok'; stdout: string; stderr: string; durationMs: number }
  | { kind: 'compile-error'; output: string }
  | { kind: 'runtime-error'; stdout: string; stderr: string; durationMs: number }
  | { kind: 'timeout'; stdout: string; stderr: string; durationMs: number };

/** sandboxd の応答を分類する（verify-lessons と同じ分類。lesson-format.md §6.1） */
export function classifyRun(res: RunResponse): RunOutcomeView {
  if (res.compile?.ok === false) return { kind: 'compile-error', output: res.compile.output ?? '' };
  const r = res.runs?.[0];
  if (!r) throw new ProtocolError('実行結果がありません');
  const base = { stdout: r.stdout, stderr: r.stderr, durationMs: r.durationMs };
  if (r.timedOut) return { kind: 'timeout', ...base };
  if (r.exitCode !== 0) return { kind: 'runtime-error', ...base };
  return { kind: 'ok', ...base };
}

function verdictOf(v: RunOutcomeView): Verdict | undefined {
  switch (v.kind) {
    case 'compile-error':
      return 'CE';
    case 'runtime-error':
      return 'RE';
    case 'timeout':
      return 'TLE';
    case 'ok':
      return undefined;
  }
}

/** `{run}` と `{experiment}` の実行（標準入力は 1 つ） */
export function runLessonCode(
  code: string,
  stdin: string,
  hooks: RunHooks = {},
): Promise<RunOutcomeView> {
  return runLesson(code, [stdin], hooks, (res) => {
    const value = classifyRun(res);
    const verdict = verdictOf(value);
    return verdict ? { value, verdict } : { value };
  });
}

/** `{write}`（card なし）の採点。全ケースを 1 リクエストで送る（lesson-format.md §4.4） */
export function gradeLessonWrite(
  code: string,
  cases: readonly JudgeCase[],
  hooks: RunHooks = {},
): Promise<JudgeResult> {
  return runLesson(
    code,
    cases.map((c) => c.in),
    hooks,
    (res) => {
      const judge = judgeCases(cases, res);
      return { value: judge, verdict: judge.overall };
    },
  );
}
